import { useState, useEffect } from "react";
import { ChevronLeft, Users, Briefcase, BarChart3, MapPin, UserX, ShieldAlert } from "lucide-react";
import StudentAttendanceTable from "../session-det/StudentAttendanceTable"; // Will act as Customer table
import StaffAttendanceTable from "../session-det/StaffAttendanceTable";
import AttendanceReportModal from "../session-det/AttendanceReportModal";
import StudentTracking from "../session-det/StudentTracking";
import UnknownAttendanceTable from "../session-det/UnknownAttendanceTable";
import SuspectsReportTable from "../session-det/SuspectsReportTable";
import { getCurrentUserRoleService } from "services/api-service";

export default function AttendanceSession() {
  const [selectedView, setSelectedView] = useState(null);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    getCurrentUserRoleService()
      .then(res => setUserRole(res.data.role_type))
      .catch(err => {
        console.error("Error fetching user role:", err);
        setUserRole("IT_ADMIN");
      });
  }, []);

  const viewConfig = {
    student: { title: "Customer Store Visits",       icon: Users,     component: StudentAttendanceTable, color: "#3b82f6" },
    staff:   { title: "Staff Attendance",            icon: Briefcase, component: StaffAttendanceTable,  color: "#8b5cf6" },
    report:  { title: "Footfall Report",             icon: BarChart3, component: AttendanceReportModal, color: "#ec4899" },
    tracking:{ title: "Customer In-Store Tracking",  icon: MapPin,    component: StudentTracking,       color: "#06b6d4" },
    unknown: { title: "Unknown Persons",             icon: UserX,     component: UnknownAttendanceTable, color: "#f97316" },
    suspect_report: { title: "Suspects Activity Report", icon: ShieldAlert, component: SuspectsReportTable, color: "#dc2626" },
  };

  if (selectedView) {
    const config = viewConfig[selectedView];
    const Component = config.component;
    const IconComponent = config.icon;

    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <div style={{
          padding: "16px 24px", background: "white",
          borderBottom: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
        }}>
          <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
            <button
              onClick={() => setSelectedView(null)}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 16px", background: "white", border: "1px solid #e2e8f0",
                borderRadius: "6px", cursor: "pointer", fontWeight: "500",
                fontSize: "13px", color: "#475569", transition: "all 0.2s",
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#cbd5e1"; }}
              onMouseOut={(e)  => { e.currentTarget.style.background = "white";   e.currentTarget.style.borderColor = "#e2e8f0"; }}
            >
              <ChevronLeft size={16} /> Back to Dashboard
            </button>
          </div>
        </div>

        <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: "40px", height: "40px", background: config.color, borderRadius: "8px"
            }}>
              <IconComponent size={22} color="white" />
            </div>
            <h1 style={{ fontSize: "28px", fontWeight: "600", color: "#0f172a", margin: 0 }}>
              {config.title}
            </h1>
          </div>
          <Component />
        </div>
      </div>
    );
  }

  const ALL_CARDS = [
    {
      view: "student", title: "Customer Store Visits",
      description: "View and manage real-time customer walk-ins and store visits",
      icon: Users, color: "#3b82f6", bgColor: "#eff6ff", adminOnly: false,
    },
    {
      view: "staff", title: "Staff Attendance",
      description: "Manage staff attendance and schedules",
      icon: Briefcase, color: "#8b5cf6", bgColor: "#faf5ff", adminOnly: true,
    },
    {
      view: "report", title: "Footfall Report",
      description: "Generate and review store visit and footfall reports",
      icon: BarChart3, color: "#ec4899", bgColor: "#fdf2f8", adminOnly: false,
    },
    {
      view: "unknown", title: "Unknown Persons",
      description: "Track unidentified persons detected across all branch cameras",
      icon: UserX, color: "#f97316", bgColor: "#fff7ed", adminOnly: true,
    },
    { view: "suspect_report", title: "Suspect Watchlist Log", description: "Track real-time detections and timestamp logs of registered suspects", icon: ShieldAlert, color: "#dc2626", bgColor: "#fef2f2", adminOnly: true }
  ];

  const cardData = ALL_CARDS.filter(c => !c.adminOnly || userRole === "IT_ADMIN");

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
      padding: "32px 24px"
    }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>

        <div style={{ marginBottom: "40px" }}>
          <h1 style={{ fontSize: "32px", fontWeight: "700", color: "#0f172a", marginBottom: "8px", letterSpacing: "-0.5px" }}>
            Store Analytics & Tracking
          </h1>
          <p style={{ fontSize: "15px", color: "#64748b", marginBottom: 0, lineHeight: "1.6" }}>
            Monitor real-time customer visits, generate footfall reports, track staff attendance,
            and monitor unknown persons or flagged suspects detected by store cameras.
          </p>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "20px",
          marginBottom: "32px"
        }}>
          {cardData.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.view}
                onClick={() => setSelectedView(card.view)}
                style={{
                  padding: "0", background: "white", border: "1px solid #e2e8f0",
                  borderRadius: "10px", cursor: "pointer", textAlign: "left",
                  transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)", overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = card.color;
                  e.currentTarget.style.boxShadow = `0 10px 25px rgba(0,0,0,0.1), 0 0 0 1px ${card.color}20`;
                  e.currentTarget.style.transform = "translateY(-4px)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div style={{
                  padding: "24px", background: card.bgColor, borderBottom: "1px solid #e2e8f0",
                  display: "flex", alignItems: "center", justifyContent: "space-between"
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: "48px", height: "48px", background: card.color, borderRadius: "8px",
                  }}>
                    <Icon size={24} color="white" />
                  </div>
                  <div style={{ fontSize: "20px", color: card.color, opacity: 0.25 }}>→</div>
                </div>
                <div style={{ padding: "20px" }}>
                  <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#0f172a", margin: "0 0 6px 0" }}>
                    {card.title}
                  </h3>
                  <p style={{ fontSize: "13px", color: "#64748b", margin: 0, lineHeight: "1.5" }}>
                    {card.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}